"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import ThemeToggle from "./ThemeToggle";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The light-mode lockup has dark navy wordmark text, which disappears
  // against a dark navbar — so in dark mode we swap in a dedicated dark
  // lockup with a light-colored wordmark instead (same treatment as the
  // footer). Both lockups now share the same canvas size, so only the
  // image src changes between themes — sizing stays identical. Wait for
  // mount so server/client markup matches.
  const isDark = mounted && resolvedTheme === "dark";

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-ink-900/[0.07] bg-white/90 backdrop-blur-md dark:border-white/10 dark:bg-ink-950/90"
          : "border-b border-transparent bg-white/60 backdrop-blur-sm dark:bg-ink-950/60"
      }`}
    >
      <nav
        aria-label="Primary"
        className="section flex h-18 items-center justify-between py-4"
      >
        <a href="#home" className="flex items-center gap-2.5">
          <Image
            src={isDark ? "/images/logo-dark-full.png" : "/images/logo-full.png"}
            alt="TalkSoon Labs"
            width={2172}
            height={724}
            priority
            className="h-8 w-auto sm:h-9"
          />
        </a>

        <div className="hidden items-center gap-6 md:flex">
          <ul className="flex items-center gap-8 text-[15px] font-medium text-ink-700 dark:text-white/70">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="transition-colors hover:text-brand-700 dark:hover:text-white"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <ThemeToggle />
          <a href="#contact" className="btn-primary">
            Book a Consultation
          </a>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label="Toggle navigation menu"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-ink-900/10 text-ink-900 dark:border-white/15 dark:text-white"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </nav>

      {open && (
        <div
          id="mobile-menu"
          className="border-t border-ink-900/[0.07] bg-white px-6 pb-6 pt-2 dark:border-white/10 dark:bg-ink-950 md:hidden"
        >
          <ul className="flex flex-col gap-1 text-[15px] font-medium text-ink-700 dark:text-white/70">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-lg px-2 py-2.5 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-white/[0.06] dark:hover:text-white"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="btn-primary mt-4 w-full"
          >
            Book a Consultation
          </a>
        </div>
      )}
    </header>
  );
}
